import React, { useEffect, useMemo, useState } from 'react';
import { BACKEND } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import TopBar from '../components/TopBar/TopBar';
import { csrfFetch } from '../utils/csrfFetch';
import styles from './RepresentativeEmployeesPage.module.css';

interface EmployeeItem {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  representative_allowed_bins?: string[];
  representative_bin_access_all?: boolean;
}

interface BinOption {
  bin: string;
  bank: string;
}

const RepresentativeEmployeesPage: React.FC = () => {
  const { role } = useAuth();
  const [employees, setEmployees] = useState<EmployeeItem[]>([]);
  const [limit, setLimit] = useState(10);
  const [availableBins, setAvailableBins] = useState<BinOption[]>([]);
  const canManageEmployees = role === 'chairman';
  const isChairman = role === 'chairman';

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [fio, setFio] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedBins, setSelectedBins] = useState<string[]>([]);
  const [allBins, setAllBins] = useState(false);
  const [editEmployee, setEditEmployee] = useState<EmployeeItem | null>(null);
  const [editSelectedBins, setEditSelectedBins] = useState<string[]>([]);
  const [editAllBins, setEditAllBins] = useState(false);

  const fullName = (e: EmployeeItem) => `${e.last_name || ''} ${e.first_name || ''}`.trim() || e.username;

  const loadEmployees = async () => {
    try {
      const res = await csrfFetch(`${BACKEND}/api/representative/employees/`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Ошибка загрузки сотрудников');
      setEmployees(Array.isArray(data.employees) ? data.employees : []);
      setLimit(typeof data.limit === 'number' ? data.limit : 10);
      const normalizedBins: BinOption[] = Array.isArray(data.available_bins)
        ? data.available_bins
            .map((item: any) => {
              if (typeof item === 'string') return { bin: item, bank: '' };
              return {
                bin: String(item?.bin || '').trim(),
                bank: String(item?.bank || '').trim(),
              };
            })
            .filter((item: BinOption) => Boolean(item.bin))
        : [];
      setAvailableBins(normalizedBins);
    } catch (error: any) {
      toast.error(error?.message || 'Ошибка загрузки сотрудников');
    }
  };

  useEffect(() => {
    if (canManageEmployees) loadEmployees();
  }, [canManageEmployees]);

  const reachedLimit = useMemo(() => employees.length >= limit, [employees.length, limit]);

  const addEmployee = async () => {
    if (reachedLimit) return toast.warning(`Лимит сотрудников: ${limit}`);

    try {
      const res = await csrfFetch(`${BACKEND}/api/representative/employees/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fio, username, password, selected_bins: selectedBins, all_bins: allBins }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Ошибка добавления сотрудника');
      toast.success('Сотрудник добавлен');
      setShowAdd(false);
      setFio('');
      setUsername('');
      setPassword('');
      setSelectedBins([]);
      setAllBins(false);
      loadEmployees();
    } catch (error: any) {
      toast.error(error?.message || 'Ошибка добавления сотрудника');
    }
  };

  const removeEmployee = async (employeeId: number) => {
    try {
      const res = await csrfFetch(`${BACKEND}/api/representative/employees/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: employeeId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Ошибка удаления сотрудника');
      toast.success('Сотрудник удален');
      loadEmployees();
    } catch (error: any) {
      toast.error(error?.message || 'Ошибка удаления сотрудника');
    }
  };

  const updateEmployeeAccess = async () => {
    if (!editEmployee) return;
    try {
      const res = await csrfFetch(`${BACKEND}/api/representative/employees/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: editEmployee.id,
          selected_bins: editSelectedBins,
          all_bins: editAllBins,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Ошибка изменения сотрудника');
      toast.success('Доступ сотрудника обновлен');
      setShowEdit(false);
      setEditEmployee(null);
      setEditSelectedBins([]);
      setEditAllBins(false);
      loadEmployees();
    } catch (error: any) {
      toast.error(error?.message || 'Ошибка изменения сотрудника');
    }
  };

  const toggleBin = (bin: string) => {
    setSelectedBins((prev) => (prev.includes(bin) ? prev.filter((item) => item !== bin) : [...prev, bin]));
  };
  const toggleEditBin = (bin: string) => {
    setEditSelectedBins((prev) => (prev.includes(bin) ? prev.filter((item) => item !== bin) : [...prev, bin]));
  };

  const labelByBin = useMemo(() => {
    const map = new Map<string, string>();
    availableBins.forEach((item) => {
      const cleanBin = String(item?.bin || '').trim();
      if (!cleanBin) return;
      const cleanBank = String(item?.bank || '').trim();
      map.set(cleanBin, cleanBank ? `${cleanBin} — ${cleanBank}` : cleanBin);
    });
    return map;
  }, [availableBins]);

  if (!canManageEmployees) return <div>Доступ только для председателя.</div>;

  return (
    <div className={styles.page}>
      <TopBar />
      <ToastContainer />

      <div className={styles.header}>
        <h1>Сотрудники представителя</h1>
        <p className={styles.subtitle}>Управление учетными записями сотрудников в стиле основных страниц.</p>
      </div>

      <div className={styles.card}>
        <p className={styles.count}>Добавлено: {employees.length} / {limit}</p>

        <div className={styles.toolbar}>
          <button
            className={styles.btn}
            onClick={() => {
              if (reachedLimit) return toast.warning(`Лимит сотрудников: ${limit}`);
              setFio('');
              setUsername('');
              setPassword('');
              setSelectedBins([]);
              setAllBins(false);
              setShowAdd(true);
            }}
          >
            Добавить
          </button>
        </div>

        <table className={styles.table}>
          <thead>
            <tr>
              <th>ФИО</th>
              <th>Логин</th>
              <th>БИН доступа</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.id}>
                <td>
                  <div className={styles.employeeCell}>
                    <span>{fullName(e)}</span>
                    {isChairman && (
                      <button
                        className={styles.btnDangerInline}
                        onClick={() => {
                          if (window.confirm(`Удалить сотрудника ${fullName(e)}?`)) {
                            removeEmployee(e.id);
                          }
                        }}
                      >
                        Удалить
                      </button>
                    )}
                  </div>
                </td>
                <td>{e.username}</td>
                <td>
                  {e.representative_bin_access_all
                    ? 'Все БИН'
                    : (e.representative_allowed_bins || []).map((bin) => labelByBin.get(bin) || bin).join(', ') || '—'}
                </td>
                <td>{e.is_active ? 'Активен' : 'Неактивен'}</td>
                <td>
                  <div className={styles.rowActions}>
                    <button
                      className={styles.btnGhost}
                      onClick={() => {
                        setEditEmployee(e);
                        setEditSelectedBins(e.representative_allowed_bins || []);
                        setEditAllBins(Boolean(e.representative_bin_access_all));
                        setShowEdit(true);
                      }}
                    >
                      Изменить
                    </button>
                    {isChairman && (
                      <button
                        className={styles.btnDanger}
                        onClick={() => {
                          if (window.confirm(`Удалить сотрудника ${fullName(e)}?`)) {
                            removeEmployee(e.id);
                          }
                        }}
                      >
                        Удалить
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!employees.length && (
              <tr>
                <td colSpan={5}>Сотрудников пока нет.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Добавить сотрудника</h3>
            <input className={styles.input} placeholder="ФИО" value={fio} autoComplete="off" onChange={(e) => setFio(e.target.value)} />
            <input className={styles.input} placeholder="Логин" value={username} autoComplete="off" onChange={(e) => setUsername(e.target.value)} />
            <input className={styles.input} placeholder="Пароль" type="password" value={password} autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} />
            <div className={styles.binHeader}>БИН из реестров председателя</div>
            <div className={styles.binList}>
              {availableBins.map((item) => (
                <label key={item.bin} className={styles.binItem}>
                  <input
                    type="checkbox"
                    checked={allBins || selectedBins.includes(item.bin)}
                    disabled={allBins}
                    onChange={() => toggleBin(item.bin)}
                  />
                  <span>{item.bank ? `${item.bin} — ${item.bank}` : item.bin}</span>
                </label>
              ))}
              {!availableBins.length && <div className={styles.binEmpty}>Нет БИН в реестрах.</div>}
            </div>
            <button
              type="button"
              className={styles.btnGhost}
              onClick={() => {
                setAllBins((prev) => !prev);
                if (allBins) setSelectedBins([]);
              }}
            >
              {allBins ? 'Отменить "Выбрать все БИН"' : 'Выбрать все БИН'}
            </button>
            <div className={styles.modalActions}>
              <button className={styles.btnGhost} onClick={() => setShowAdd(false)}>Отмена</button>
              <button className={styles.btn} onClick={addEmployee}>Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {showEdit && editEmployee && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Изменить доступ: {fullName(editEmployee)}</h3>
            <div className={styles.binHeader}>Доступные БИН</div>
            <div className={styles.binList}>
              {availableBins.map((item) => (
                <label key={item.bin} className={styles.binItem}>
                  <input
                    type="checkbox"
                    checked={editAllBins || editSelectedBins.includes(item.bin)}
                    disabled={editAllBins}
                    onChange={() => toggleEditBin(item.bin)}
                  />
                  <span>{item.bank ? `${item.bin} — ${item.bank}` : item.bin}</span>
                </label>
              ))}
              {!availableBins.length && <div className={styles.binEmpty}>Нет БИН в реестрах.</div>}
            </div>
            <button
              type="button"
              className={styles.btnGhost}
              onClick={() => {
                setEditAllBins((prev) => !prev);
                if (editAllBins) setEditSelectedBins([]);
              }}
            >
              {editAllBins ? 'Отменить "Выбрать все БИН"' : 'Выбрать все БИН'}
            </button>
            <div className={styles.modalActions}>
              <button
                className={styles.btnGhost}
                onClick={() => {
                  setShowEdit(false);
                  setEditEmployee(null);
                  setEditSelectedBins([]);
                  setEditAllBins(false);
                }}
              >
                Отмена
              </button>
              <button className={styles.btn} onClick={updateEmployeeAccess}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepresentativeEmployeesPage;
